import React, { useEffect, useRef, useState } from "react";

import USPSection from "./app/USPSection"; // Adjust the path based on where you save the component

import { useParams } from "react-router-dom";
import {
  Container,
  Typography,
  Grid,
  Box,
  useTheme,
  useMediaQuery,
  Card,
  IconButton,
  CardContent,
  Chip,
} from "@mui/material";
import { features } from "./app/functions";
import {
  StyledButton,
  ResponsiveVideo,
  FeatureSection,
  FeatureList,
  GradientBackground,
  StepCircle,
} from "./StyledComponents";
import { Link } from "react-router-dom";
import OurModels from "./app/OurModels";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { TypeAnimation } from "react-type-animation";
import OurLanguages from "./app/OurLanguages";
import Header from "./Header";
import StepProcessSection from "./StepProcessSection";

import { getStyles } from "./index";
import AIAct from "./AIAct";

import { East as ArrowIcon } from "@mui/icons-material";

import ContactForm from "./ContactForm";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import PersonIcon from "@mui/icons-material/Person";
import SchoolIcon from "@mui/icons-material/School";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import Footer from "./Footer";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { motion } from "framer-motion";
import CustomInputSwitch from "./app/CustomInputSwitch";

import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import { getAIModelImage } from "./app/TestComponents2";
import UniversitySection from "./UniversitySection";
import MediaSection from "./MediaSection";
import { BenefitsSection } from "./FeaturePageEnhancements";
import { HelmetProvider, Helmet } from "react-helmet-async";
import FunctionsSwitcher from "./app/FunctionsSwitcher";
import FunctionsSwitcherGrid from "./app/FunctionsSwitcherGrid";
import FunctionsSwitcherHoriz from "./app/FunctionsSwitcherHoriz";

import Logger from "./app/Logger";

import { InstructionItem } from "./app/functions";
import {
  lessonItems,
  quizItems,
  flashcardItems,
  textLabbetItems,
  gradingItems,
  powerpointItems,
  assistentItems,
  visionItems,
  imageItems,
  lessonPlanItems,
  bibleItems,
  VideoPlayer,
  podcastItems,
} from "./app/HelpDialogItems";

import {
  quizItemsStudent,
  flashcardItemsStudent,
  assistentItemsStudent,
  gradingItemsStudent,
  imageItemsStudent,
  podcastItemsStudent,
  hpItemsStudent,
} from "./app/HelpDialogItemsStudents";

const FeaturePage = ({ toggleDarkMode, isDark }) => {
  const contactFormRef = useRef(null); // Add ref for ContactForm
  const { featureId } = useParams();

  // With this code:
  const [selectedUserType, setSelectedUserType] = useState(() => {
    // Try to get stored value from localStorage
    const storedValue = localStorage.getItem("selectedUserType");
    // Return stored value if it exists, otherwise default to 1 (Teacher)
    return storedValue !== null ? parseInt(storedValue, 10) : 0;
  });

  // Add this useEffect to save the selection to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("selectedUserType", selectedUserType.toString());
  }, [selectedUserType]);

  const feature = features.find((f) => {
    if (!f.url) return false; // Guard against missing URL property

    return (
      f.url
        .toLowerCase()
        .replace(/ /g, "-")
        .replace(/[åä]/g, "a") // Simplified regex for multiple characters
        .replace(/ö/g, "o") === featureId
    );
  });

  Logger.log("feature: ", feature);
  const theme = useTheme();

  const styles = getStyles(theme);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isVideo = (url) => url.endsWith(".mp4");

  const scrollToContact = (e) => {
    e.preventDefault();
    contactFormRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [featureId]);

  if (!feature) {
    return <Typography>Feature not found</Typography>;
  }

  return (
    <>
      <Helmet>
        <title>{`${feature.titleSEO} | Studera.ai`}</title>
        <meta name="description" content={feature.descriptionSEO} />
      </Helmet>

      <Header contactFormRef={contactFormRef} />

      <GradientBackground className="gradient-background">
        <Container
          sx={{
            pt: { xs: 10, md: 20 },
            pb: { xs: 6, md: 14 },
            position: "relative",
            textAlign: "center",
            width: { xs: "100%", md: "70%" },
          }}
        >
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={12} sx={{ textAlign: "center" }}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                  <Chip
                    label={
                      <span>
                        Studera.ai{" "}
                        <span style={{ opacity: 0.7 }}>
                          &nbsp;•&nbsp; Framtidens lärande
                        </span>
                      </span>
                    }
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      color: "white",
                      fontWeight: "600",
                      fontSize: "0.8rem",
                      padding: "2px 14px",
                      mb: { xs: 1, md: 4 },
                      borderRadius: "16px",
                      "& .MuiChip-label": {
                        padding: "0 4px",
                      },
                    }}
                  />
                </Box>

                <Typography
                  variant="h2"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    width: { xs: "100%", md: "90%" },
                    fontSize: { xs: "2.1rem", md: "3.5rem" },
                    margin: "0 auto",
                  }}
                >
                  {feature.altTitle ? feature.altTitle : feature.title}
                </Typography>
                {feature.url === "bedomning" ? (
                  <>
                    {!isMobile && (
                      <Typography
                        variant="h5"
                        sx={{
                          fontSize: { xs: "1rem", md: "1.2rem" },
                          lineHeight: { xs: "1.6rem", md: "2.2rem" },
                          fontWeight: { xs: "400", md: "400" },
                          color: "#e5e5e5",
                          width: { xs: "100%", md: "90%" },
                          margin: "0 auto",
                          mt: 3,
                          mb: 3,
                        }}
                      >
                        {feature.descriptionLonger}
                      </Typography>
                    )}

                    <USPSection />
                  </>
                ) : (
                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: { xs: "1rem", md: "1.2rem" },
                      lineHeight: { xs: "1.6rem", md: "2.2rem" },
                      fontWeight: { xs: "400", md: "400" },
                      color: "#e5e5e5",
                      width: { xs: "100%", md: "90%" },
                      margin: "0 auto",
                      mt: 3,
                      mb: 3,
                    }}
                  >
                    {feature.descriptionLonger}
                  </Typography>
                )}
                {/*
                <Typography
                  variant='h5'
                  sx={{
                    mb: 4,
                    opacity: 0.8,
                    fontSize: '1.3rem',
                    fontWeight: '600',
                    mt: 5,
                    display: { xs: 'none', md: 'block' },
                  }}
                >

                  <TypeAnimation
                    sequence={feature.typeAnimationSequence || []}
                    speed={50}
                    deletionSpeed={90}
                    style={{ fontSize: '1.3rem' }}
                    repeat={Infinity}
                  />

                </Typography>
                */}

                <Box sx={{ textAlign: "center", mt: 9 }}>
                  <Link
                    to="/app/skapa-konto?ref=startpage"
                    style={{ textDecoration: "none" }}
                  >
                    <StyledButton
                      variant="blue"
                      color="primary"
                      size="large"
                      sx={{
                        borderRadius: "100px",
                        padding: "13px 50px",
                        fontSize: { xs: "17px", md: "20px" },
                        width: { xs: "290px", md: "340px" },
                        fontWeight: "600",

                        backgroundColor: "#eeeeee",
                        borderBottom: "none",
                        color: "#221562",
                        "&:hover": {
                          backgroundColor: "white",
                          color: "#333",
                        },
                      }}
                    >
                      Skapa konto gratis{" "}
                      {!isMobile && (
                        <KeyboardDoubleArrowRightIcon sx={{ ml: 1 }} />
                      )}
                    </StyledButton>
                  </Link>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </GradientBackground>
      <UniversitySection bgcolor="background.default" compact={true} />
      {feature.url === "bedomning" && (
        <>
          <StepProcessSection
            isDark={theme.palette.mode === "dark"}
            chipLabel="SÅ FUNKAR DET"
            title="En enkel process."
            subtitle="Inga tekniska kunskaper krävs"
            steps={[
              {
                title: "Välj bedömningsmatris",
                description:
                  "Välj bland våra färdiga bedömningsmatriser eller generera din egen matris utifrån din uppgift.",
              },
              {
                title: "Ladda upp texter",
                description:
                  "Ladda upp en eller flera elevtexter och få dem automatiskt analyserade och bedömda på ett par minuter.",
              },
              {
                title: "Få din bedömning",
                description:
                  "Se omfattande feedback med poängbedömning, konkreta förbättringsförslag och kommentarer som du enkelt kan dela med dina elever.",
              },
            ]}
          />
        </>
      )}

      {/* Check if the current feature has student-specific content */}
      {["quiz", "flashcards", "bedomning", "assistenter", "bilder"].includes(
        feature.url,
      ) ? (
        <>
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{
              fontWeight: "600",
              mt: 10,
              fontSize: { xs: "1.8rem", md: "1.7em" },
            }}
          >
            Visa funktioner för:
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: { xs: 5, md: 10 },
              mt: 4,
            }}
          >
            <CustomInputSwitch
              selectedIndex={selectedUserType}
              onIndexChange={setSelectedUserType}
              options={["Lärare", "Elever"]}
              optionIcons={[<SchoolIcon />, <PersonOutlineIcon />]}
              theme={theme}
              width="430px"
              mobileWidth="90%"
              height="60px"
              fontSize="1.2rem"
              iconSize="1.2em"
              iconSpacing="16px"
              borderRadius="50px"
              nonActiveColor={
                theme.palette.mode === "dark" ? "#3a454f" : "white"
              }
            />
          </Box>
        </>
      ) : null}
      {(() => {
        // Map feature URLs to their corresponding item arrays
        const itemsMap = {
          flashcards:
            selectedUserType === 1 ? flashcardItemsStudent : flashcardItems,
          textlabbet: textLabbetItems,

          powerpoint: powerpointItems,
          assistenter:
            selectedUserType === 1 ? assistentItemsStudent : assistentItems,
          "vision-ai": visionItems,
          hjalpmedel: visionItems,
          bilder: selectedUserType === 1 ? imageItemsStudent : imageItems,
          lektionsplanering: lessonPlanItems,
          podcast: selectedUserType === 1 ? podcastItemsStudent : podcastItems,
          hogskoleprovet: hpItemsStudent,
          bibeln: bibleItems,
        };

        // Get the items array based on the current feature URL
        const items = itemsMap[feature.url];

        // Render the section if items are found for the current URL
        return items ? (
          <Box
            sx={{
              py: { xs: 6, md: 10 },

              maxWidth: "1300px",
              margin: "0 auto",

              bgcolor: "background.default",
            }}
          >
            {items
              .filter((item) => !item.hide)
              .map((item, index) => (
                <Grid
                  container
                  spacing={10}
                  alignItems="center"
                  sx={{ mb: { xs: 8, md: 30 }, p: { xs: 2, md: 0 } }}
                  key={index}
                  direction={index % 2 === 0 ? "row" : "row-reverse"}
                >
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="overline"
                      sx={{
                        color: "primary.main",
                        fontWeight: 600,
                        mb: 0,
                        display: "block",
                        fontSize: "1.2rem",
                        display: "none",
                      }}
                    >
                      {item.category || "Quiz/Prov med AI"}
                    </Typography>
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: 800,
                        mb: 2,
                        fontSize: { xs: "2rem", md: "2.8rem" },
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        mb: 4,
                        color: "text.secondary",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.7,
                        whiteSpace: "pre-line",
                      }}
                    >
                      {item.description}
                    </Typography>

                    {item.listItems?.map((listItem, index) => (
                      <InstructionItem
                        text={listItem.text}
                        marginBottom={1}
                        simple="true"
                        fontWeight="400"
                        color="text.secondary"
                      />
                    ))}

                    {item.customContent && item.customContent}
                    {item.link && (
                      <Link to={item.link} style={{ textDecoration: "none" }}>
                        <StyledButton
                          variant="blue"
                          size="large"
                          sx={{
                            borderRadius: "12px",
                            padding: "12px 32px",
                            fontSize: "1.1rem",
                          }}
                          endIcon={<KeyboardDoubleArrowRightIcon />}
                        >
                          {item.buttonText || "Läs mer"}
                        </StyledButton>
                      </Link>
                    )}
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                      paddingTop: {
                        xs: "25px !important",
                        md: "80px !important",
                      },
                    }}
                  >
                    {item.videoUrl ? (
                      <VideoPlayer
                        key={item.videoUrl}
                        videoUrl={item.videoUrl}
                        poster={item.poster}
                      />
                    ) : item.image ? (
                      <Box
                        component="img"
                        src={item.image}
                        alt={item.title}
                        sx={{
                          width: "100%",
                          height: "auto",
                          borderRadius: "12px",
                          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                          transition: "transform 0.3s ease-in-out",
                          "&:hover": {
                            transform: "translateY(-10px)",
                          },
                        }}
                      />
                    ) : null}
                  </Grid>
                </Grid>
              ))}
          </Box>
        ) : null;
      })()}
      {feature.url === "bedomning" && (
        <>
          <Container
            maxWidth="xl"
            sx={{ mb: { xs: 0, md: 15 }, maxWidth: "1300px" }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 2,
                fontSize: { xs: "2rem", md: "2.8rem" },
              }}
            >
              Bedömning med AI
            </Typography>
            <Box sx={{ mb: 6 }}>
              <Typography
                variant="subtitle"
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "1rem", md: "1.3rem" },
                  lineHeight: 1.7,
                  whiteSpace: "pre-line",
                }}
              >
                Bedöm hela klassens texter med AI baserat på vår eller din egen
                bedömningsmatris. Få detaljerad feedback för varje
                bedömningskriterie med möjlighet att redigera, skriva ut & dela.
              </Typography>
            </Box>
            <FunctionsSwitcher
              functions={
                selectedUserType === 1
                  ? gradingItemsStudent.filter(
                      (item) => ![""].includes(item.title),
                    )
                  : gradingItems.filter(
                      (item) =>
                        ![
                          "Generera bedömningsmatris",
                          "Dela matris med kollegor",
                        ].includes(item.title),
                    )
              }
            />
          </Container>

          {selectedUserType === 0 && (
            <Container maxWidth="xl" sx={{ py: 15, maxWidth: "1300px" }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  fontSize: { xs: "2rem", md: "2.8rem" },
                }}
              >
                Generera egna matriser
              </Typography>
              <Box sx={{ mb: 4, width: { xs: "100%", md: "70%" } }}>
                <Typography
                  variant="subtitle"
                  sx={{
                    color: "text.secondary",
                    fontSize: { xs: "1rem", md: "1.3rem" },
                    lineHeight: 1.7,
                    whiteSpace: "pre-line",
                  }}
                >
                  Skriv in vilken typ av uppgift som bedömningsmatrisen ska
                  bedöma, och få en helt egen matris baserat på uppgiften.
                  Använd sedan matrisen för din bedömning.
                </Typography>
              </Box>
              <FunctionsSwitcherGrid
                functions={gradingItems.filter((item) =>
                  [
                    "Generera bedömningsmatris",
                    "Dela matris med kollegor",
                  ].includes(item.title),
                )}
              />
            </Container>
          )}
          <AIAct theme={theme} styles={styles} />
        </>
      )}

      {feature.url === "quiz" && (
        <>
          <Container maxWidth="xl" sx={{ mb: 20, maxWidth: "1300px" }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 2,
                fontSize: { xs: "2rem", md: "2.8rem" },
              }}
            >
              Generera quiz med AI
            </Typography>
            <Box sx={{ mb: 6, width: { xs: "100%", md: "70%" } }}>
              <Typography
                variant="subtitle"
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "1rem", md: "1.3rem" },
                  lineHeight: 1.7,
                  whiteSpace: "pre-line",
                }}
              >
                Generera ett quiz med AI baserat på eget underlag eller valfria
                instruktioner. Redigera, spara och dela med eleverna samt
                övervaka resultaten i realtid.
              </Typography>
            </Box>
            <FunctionsSwitcher
              functions={
                selectedUserType === 1
                  ? quizItemsStudent.filter(
                      (item) =>
                        ![
                          "Dela med vänner",
                          "Välj mellan olika underlag",
                          "Redigera quiz",
                          "Öva under tidspress med timer",
                          "Simulera prov med bakgrundsljud",
                        ].includes(item.title),
                    )
                  : quizItems.filter(
                      (item) =>
                        ![
                          "Dela via provnyckel (inget konto)",
                          "Dela via elevrum (konto krävs)",
                          "Diskutera med AI",
                          "Ledtrådar från AI",
                          "Öva under tidspress med timer",
                          "Simulera prov med bakgrundsljud",
                        ].includes(item.title),
                    )
              }
            />
          </Container>

          {selectedUserType === 0 && (
            <>
              <Container maxWidth="xl" sx={{ mb: 20, maxWidth: "1300px" }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 800,
                    mb: 2,
                    fontSize: { xs: "2rem", md: "2.8rem" },
                  }}
                >
                  Dela quiz med elever & övervaka resultat
                </Typography>
                <Box sx={{ mb: 4, width: { xs: "100%", md: "70%" } }}>
                  <Typography
                    variant="subtitle"
                    sx={{
                      color: "text.secondary",
                      fontSize: { xs: "1rem", md: "1.3rem" },
                      lineHeight: 1.7,
                      whiteSpace: "pre-line",
                    }}
                  >
                    Du kan enkelt dela ditt genererade quiz med elever. Antingen
                    via en provnyckel (inget elevkonto krävs) eller dela direkt
                    via Studera.ai (elevkonto krävs).
                  </Typography>
                </Box>
                <FunctionsSwitcherGrid
                  functions={quizItems.filter((item) =>
                    [
                      "Dela via provnyckel (inget konto)",
                      "Dela via elevrum (konto krävs)",
                    ].includes(item.title),
                  )}
                />
              </Container>
            </>
          )}
        </>
      )}

      {feature.url === "lektioner" && (
        <>
          <StepProcessSection
            chipLabel=""
            title="Så funkar det"
            subtitle=""
            isDark={theme.palette.mode === "dark"}
            steps={[
              {
                title: "Välj underlag",
                description:
                  "Basera lektionen på valfria instruktioner, dokument, bild, artikel från Wikipedia eller Skolverkets kursplan",
              },
              {
                title: "Generera lektionen",
                description:
                  "Låt vår AI generera lektionen baserat på ditt underlag. Lägg till övningar, uppgifter, quiz eller bilder.",
              },
              {
                title: "Dela med elever",
                description:
                  "Dela din genererade lektion med dina elever via en delbar länk eller via Studera.ai",
              },
            ]}
          />

          <Container
            maxWidth="xl"
            sx={{
              mb: { xs: 5, md: 20 },
              mt: { xs: 5, md: 10 },
              maxWidth: "1300px",
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 2,
                fontSize: { xs: "2rem", md: "2.8rem" },
              }}
            >
              Generera lektioner med AI
            </Typography>
            <Box sx={{ mb: 6, width: { xs: "100%", md: "70%" } }}>
              <Typography
                variant="subtitle"
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "1rem", md: "1.3rem" },
                  lineHeight: 1.7,
                  whiteSpace: "pre-line",
                }}
              >
                Generera lektioner med AI baserat på eget underlag eller valfria
                instruktioner. Redigera, spara och dela med eleverna.
              </Typography>
            </Box>
            <FunctionsSwitcher
              functions={lessonItems.filter(
                (item) =>
                  ![
                    "Dela via länk (inget konto krävs)",
                    "Dela via elevrum (konto krävs)",
                    "Generera övningar",
                    "Generera frågor",
                    "Generera tidslinje",
                  ].includes(item.title),
              )}
            />
          </Container>
          <Container maxWidth="xl" sx={{ mb: 20, maxWidth: "1300px" }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 2,
                fontSize: { xs: "2rem", md: "2.8rem" },
              }}
            >
              Dela lektioner med elever
            </Typography>
            <Box sx={{ mb: 4, width: { xs: "100%", md: "70%" } }}>
              <Typography
                variant="subtitle"
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "1rem", md: "1.3rem" },
                  lineHeight: 1.7,
                  whiteSpace: "pre-line",
                }}
              >
                Du kan enkelt dela ditt genererade material med elever. Antingen
                via en länk (inget elevkonto krävs) eller dela direkt via
                Studera.ai (elevkonto krävs).
              </Typography>
            </Box>
            <FunctionsSwitcherHoriz
              functions={lessonItems.filter((item) =>
                [
                  "Dela via länk (inget konto krävs)",
                  "Dela via elevrum (konto krävs)",
                ].includes(item.title),
              )}
            />
          </Container>

          <Container maxWidth="xl" sx={{ mb: 20, maxWidth: "1300px" }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 2,
                fontSize: { xs: "2rem", md: "2.8rem" },
              }}
            >
              Anpassa lektionen
            </Typography>
            <Box sx={{ mb: 4, width: { xs: "100%", md: "70%" } }}>
              <Typography
                variant="subtitle"
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "1rem", md: "1.3rem" },
                  lineHeight: 1.7,
                  whiteSpace: "pre-line",
                }}
              >
                När du har genererat en lektion kan du enkelt generera fler
                sektioner med AI, exempelvis övningar, uppgifter och frågor.
              </Typography>
            </Box>
            <FunctionsSwitcherGrid
              functions={lessonItems.filter((item) =>
                ["Generera frågor", "Generera övningar"].includes(item.title),
              )}
            />
          </Container>
          <Container maxWidth="xl" sx={{ mb: 15, maxWidth: "1300px" }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 2,
                fontSize: { xs: "2rem", md: "2.8rem" },
              }}
            >
              Välj mellan 30 olika lektionstyper
            </Typography>
            <Typography
              variant="subtitle"
              sx={{
                mb: 4,
                color: "text.secondary",
                fontSize: { xs: "1rem", md: "1.3rem" },
                lineHeight: 1.7,
                whiteSpace: "pre-line",
              }}
            >
              Generera allt från läsförståelse, tidslinjer, tankekartor till på
              spåret, jeopardy och mycket mer.
            </Typography>
            <Box sx={{ mt: 4 }}>
              <VideoPlayer
                videoUrl="https://sportkuriren.se/wp-content/uploads/lektioner-typer-3.mp4"
                autoplayOnScroll={true}
              />
            </Box>
          </Container>
          <Container maxWidth="xl" sx={{ mb: 15, maxWidth: "1300px" }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 2,
                fontSize: { xs: "2rem", md: "2.8rem" },
              }}
            >
              Smidig kontrollpanel
            </Typography>
            <Typography
              variant="subtitle"
              sx={{
                mb: 4,
                color: "text.secondary",
                fontSize: { xs: "1rem", md: "1.3rem" },
                lineHeight: 1.7,
                whiteSpace: "pre-line",
              }}
            >
              Hantera din lektion från en smidig kontrollpanel
            </Typography>
            <Box sx={{ mt: 4 }}>
              <VideoPlayer
                videoUrl="https://sportkuriren.se/wp-content/uploads/control-panel4.mp4"
                autoplayOnScroll={true}
              />
            </Box>
          </Container>
        </>
      )}

      {feature.url === "quiz" && (
        <>
          <Container maxWidth="xl" sx={{ mb: 20, maxWidth: "1300px" }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 2,
                fontSize: { xs: "2rem", md: "2.8rem" },
              }}
            >
              Flera frågetyper
            </Typography>
            <Typography
              variant="subtitle"
              sx={{
                mb: 4,
                color: "text.secondary",
                fontSize: { xs: "1rem", md: "1.3rem" },
                lineHeight: 1.7,
                whiteSpace: "pre-line",
              }}
            >
              Välj mellan skriftliga prov, flervalsfrågor m.m.
            </Typography>
            <Box sx={{ mt: 4 }}>
              <VideoPlayer
                videoUrl="https://sportkuriren.se/wp-content/uploads/quiz-fragetyper.mp4"
                autoPlay={true}
              />
            </Box>
          </Container>

          <Container maxWidth="xl" sx={{ mb: 15, maxWidth: "1300px" }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 2,
                fontSize: { xs: "2rem", md: "2.8rem" },
              }}
            >
              Exempel på frågetyper
            </Typography>
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="subtitle"
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "1rem", md: "1.3rem" },
                  lineHeight: 1.7,
                  whiteSpace: "pre-line",
                }}
              >
                Du kan välja mellan olika frågeformat som skriftliga prov,
                flervalsfrågor, lucktext, sant/falskt och nutidsfrågor. Dessa
                kan användas för att testa olika kunskapsområden, anpassa
                svårighetsgraden och skapa variation i dina quiz.
              </Typography>
            </Box>
            <FunctionsSwitcher
              functions={[
                {
                  title: "Flervalsfrågor",
                  subtitle:
                    "Flervalsfrågor med ett korrekt svar där du väljer det alternativ som bäst besvarar frågan.",
                  videoUrl:
                    "https://sportkuriren.se/wp-content/uploads/quiz-flervalsfragor-2.mp4",
                  poster:
                    "https://sportkuriren.se/wp-content/uploads/quiz-flervalsfragor.png",
                },
                {
                  title: "Skriftligt prov",
                  subtitle:
                    "Simulera ett riktigt prov där du får visa dina kunskaper genom att formulera egna, utförliga svar.",
                  videoUrl:
                    "https://sportkuriren.se/wp-content/uploads/quiz-prov-2.mp4",
                  poster:
                    "https://sportkuriren.se/wp-content/uploads/quiz-prov.png",
                },
                {
                  title: "Lucktext",
                  subtitle:
                    "Välj rätt utelämnat ord i frågan för att visa att du förstår kontexten.",
                  videoUrl:
                    "https://sportkuriren.se/wp-content/uploads/quiz-fyll-i-luckan-2.mp4",
                  poster:
                    "https://sportkuriren.se/wp-content/uploads/quiz-fyll-i-luckan.png",
                },
                {
                  title: "Sant/Falskt",
                  subtitle:
                    "Påståenden som är sanna eller falska och kräver att du bedömer informationens korrekthet.",
                  videoUrl:
                    "https://sportkuriren.se/wp-content/uploads/quiz-sant-falskt-2.mp4",
                  poster:
                    "https://sportkuriren.se/wp-content/uploads/quiz-sant-falskt.png",
                },
                {
                  title: "Nutidsfrågor",
                  subtitle:
                    "Traditionella nutidsfrågor i 1X2-format med svarstalong och facit.",
                  videoUrl:
                    "https://sportkuriren.se/wp-content/uploads/quiz-nutidsfragor-4.mp4",
                  poster:
                    "https://sportkuriren.se/wp-content/uploads/quiz-nutidsfragor-3.png",
                },
              ]}
            />
          </Container>
          <Container maxWidth="xl" sx={{ mb: 20, maxWidth: "1300px" }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 2,
                fontSize: { xs: "2rem", md: "2.8rem" },
              }}
            >
              Det finns mer
            </Typography>
            <Box sx={{ mb: 4, width: { xs: "100%", md: "70%" } }}>
              <Typography
                variant="subtitle"
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "1rem", md: "1.3rem" },
                  lineHeight: 1.7,
                  whiteSpace: "pre-line",
                }}
              >
                Utforska fler funktioner. Vi lägger ständigt till nya!
              </Typography>
            </Box>
            <FunctionsSwitcherHoriz
              functions={quizItems.filter((item) =>
                [
                  "Generera quiz från bild",
                  "Skriv ut quiz",
                  "Öva under tidspress med timer",
                  "Simulera prov med bakgrundsljud",
                ].includes(item.title),
              )}
            />
          </Container>
        </>
      )}

      <Box sx={{ bgcolor: "background.default", py: 8, mt: 8 }}>
        <Container
          maxWidth="xl"
          sx={{ mb: { xs: 5, md: 20 }, maxWidth: "1300px" }}
        >
          <OurModels
            subtitle={
              feature.url === "bedomning"
                ? "När du bedömer texter kan du välja mellan följande AI-modeller"
                : "När du genererar material kan du välja mellan följande modeller"
            }
            styles={styles}
          />
        </Container>
      </Box>

      <Box sx={{ bgcolor: "background.default", py: 8, mt: 8 }}>
        <Container
          maxWidth="xl"
          sx={{ mb: { xs: 5, md: 20 }, maxWidth: "1300px" }}
        >
          <OurLanguages
            title="Språk vi stöjder"
            subtitle={
              feature.url === "bedomning"
                ? "När du bedömer en text kan du få bedömningen på ett av följande språk"
                : "När du genererar innehålle kan välja mellan följande språk"
            }
            theme={theme}
            styles={styles}
          />
        </Container>
      </Box>

      <Container
        maxWidth="xl"
        sx={{
          paddingTop: { xs: 10, md: 22 },
          paddingBottom: { xs: 10, md: 22 },
          textAlign: "center",

          backgroundImage:
            'url("/img/hepp.svg"), radial-gradient(circle, rgb(106, 80, 243), rgb(62, 46, 141) 82%)',
        }}
      >
        <Typography
          variant="h3"
          gutterBottom
          sx={{
            fontWeight: "bold",
            fontSize: "3.2em",
            color: "white",
            display: "block",
            mb: 3,
          }}
        >
          Prova helt gratis
        </Typography>
        <Typography variant="h6" sx={{ mb: 4, color: "#E7E7E8" }}>
          Anslut dig till 43,000+ lärare och elever som använder Studera.ai
        </Typography>
        <Link
          to="/app/skapa-konto?ref=startpage"
          style={{ textDecoration: "none" }}
        >
          <StyledButton
            variant="blue"
            color="primary"
            size="large"
            sx={{
              borderRadius: "100px",
              padding: "13px 50px",
              fontSize: "20px",

              fontWeight: "600",
              mt: 4,
              backgroundColor: "#eeeeee",
              borderBottom: "none",
              color: "#221562",
              "&:hover": {
                backgroundColor: "white",
                color: "#333",
              },
            }}
          >
            Skapa konto gratis
          </StyledButton>
        </Link>
      </Container>

      <Box
        sx={{
          py: { xs: 6, md: 17 },
          bgcolor: "background.default",
          display: "none",
        }}
      >
        <Container>
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{
              fontWeight: "bold",

              fontSize: "2.6em",
            }}
          >
            Funktioner
          </Typography>
          <Grid container spacing={5} sx={{ mt: { xs: 2, md: 7 } }}>
            {feature.details?.map((detail, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.1)",
                    borderRadius: 3,
                    transition: "transform 0.3s",

                    "&:hover": {
                      transform: "translateY(-10px)",
                    },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        mb: 4,
                        mt: 2,
                      }}
                    >
                      {React.cloneElement(detail.icon, {
                        fontSize: "large",
                        sx: {
                          fontSize: "50px",
                          color:
                            theme.palette.mode === "dark"
                              ? "#9575F7"
                              : "#6424c1",
                        },
                      })}
                    </Box>
                    <Typography
                      variant="h5"
                      align="center"
                      gutterBottom
                      sx={{ fontWeight: "bold" }}
                    >
                      {detail.text.split(":")[0]}
                    </Typography>
                    <Typography
                      variant="body1"
                      align="center"
                      color="text.secondary"
                    >
                      {detail.text.split(":")[1]}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      <div ref={contactFormRef}>
        <ContactForm showMoreFields={true} />
      </div>

      <Box
        sx={{
          py: { xs: 6, md: 17, display: "none" },
          bgcolor: "background.paper",
        }}
      >
        <Container sx={{ textAlign: "center" }}>
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ fontWeight: "bold", fontSize: "2.6em" }}
          >
            Lär dig mer
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mt: { xs: 2, md: 7 },

              mb: 4,
              color: "text.secondary",
              fontWeight: "400",
              fontSize: "1.15rem",
              lineHeight: "1.6",
              whiteSpace: "pre-line",
            }}
          >
            {feature.aboutText}
          </Typography>

          <Link to="/app/skapa-konto" style={{ textDecoration: "none" }}>
            <StyledButton
              variant="contained"
              color="primary"
              size="large"
              endIcon={<ArrowForwardIcon />}
            >
              Börja din resa nu
            </StyledButton>
          </Link>
        </Container>
      </Box>

      <MediaSection bgcolor="background.paper" />

      <Footer />

      {!isMobile && (
        <Box
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 9999,
          }}
        >
          <IconButton onClick={toggleDarkMode}>
            {isDark ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Box>
      )}
    </>
  );
};

export default FeaturePage;
